/**
 * /policies/shifts/biometric-devices — Session 3c PR 12.
 * Owning agent: Tashghil.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { BiometricDevicePolicyItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function ShiftBiometricDevicesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-shift-biometric-devices", le],
    queryFn: () => session3cApi.listBiometricDevicesPolicy(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<BiometricDevicePolicyItem>[] = [
    {
      headerKey: "policies.biometric_devices.col.device",
      label: "Device",
      render: (r) => <span className="font-mono text-xs">{r.device_name}</span>,
    },
    {
      headerKey: "policies.biometric_devices.col.location",
      label: "Location",
      render: (r) => <span className="text-text-secondary">{r.location_label}</span>,
    },
    {
      headerKey: "policies.biometric_devices.col.schedule",
      label: "Sync schedule",
      render: (r) => <span className="font-mono text-[10px] text-text-tertiary">{r.sync_schedule_cron}</span>,
    },
    {
      headerKey: "policies.biometric_devices.col.last_sync",
      label: "Last sync",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.last_sync_at ?? "—"}</span>,
    },
    {
      headerKey: "policies.biometric_devices.col.status",
      label: "Status",
      render: (r) => <span className="text-xs">{r.status}</span>,
    },
  ];

  return (
    <PolicyListPage<BiometricDevicePolicyItem>
      title={t("policies.biometric_devices.title", "Biometric Device Catalog")}
      subtitle={t("policies.biometric_devices.subtitle", "ZK device register · per-device sync schedule · location · IP/serial config.")}
      agent="tashghil"
      domain="biometric_devices"
      newButtonLabel={t("policies.biometric_devices.cta.new", "Register New Device")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.biometric_devices.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.biometric_devices.suggestion.clean_body", "Device sync health lives on /operations/attendance/biometric-sync. This catalog is the source for which devices the scanner queries."),
          primaryActionLabel: t("policies.biometric_devices.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "shift-biometric-devices-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.biometric_devices.title", "Biometric Device Catalog"),
        window_label: t("policies.biometric_devices.window", "across all locations"),
      }}
    />
  );
}
